import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { Query, Mutation } from 'react-apollo';
import { FormattedMessage } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import '../../style/ProjectsList.scss';
import '../../style/DisplaysList.scss'; // réutilise .tsk-displays-toolbar / .tsk-input-search
import queries from "../../queries";
import NeedAdmin from '../NeedAdmin';
import confirmDialog from '../dialogs/ConfirmDialog';
import { showToast } from '../../toast';
import { t, tn } from '../../i18n';
import LoadingHint from '../LoadingHint';
import { getFavorites, toggleFavorite, getRecents, touchRecent } from '../../userPrefs';

// Noms des appareils sur lesquels l'interface est déployée, ou "Not deployed".
function deploymentLabel(project) {
  const devices = project.devices || [];
  if (devices.length === 0) return t('ui.list.not_deployed');
  return devices.map((d) => d.name || d.location || d.id).join(', ');
}

// Nombre d'interfaces affichées d'un coup (la liste peut en contenir plus d'un millier).
const PAGE_SIZE = 60;

const isDeployed = (p) => (p.devices || []).length > 0;

const STATUS_FILTERS = [
  { id: 'all', label: t('ui.list.filter.all') },
  { id: 'online', label: t('ui.list.filter.online') },
  { id: 'idle', label: t('ui.list.filter.idle') },
];

const SORTS = [
  { id: 'name', label: t('ui.list.sort.name') },
  { id: 'status', label: t('ui.list.sort.status') },
  { id: 'screens', label: t('ui.list.sort.screens') },
];

class ProjectsList extends Component {

  constructor(props) {
    super(props);
    this.state = {
      showArchived: false,
      search: "",
      status: 'all',
      sort: 'name',
      favorites: getFavorites(),
      visible: PAGE_SIZE,
    };
  }

  onToggleFavorite = (e, projectId) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ favorites: toggleFavorite(projectId) });
  };

  deleteButton(project) {
    return (
      <button
        type='button'
        className='tsk-button tsk-small tsk-projects-icon-btn tsk-projects-delete-btn'
        title={t('ui.list.delete')}
        aria-label={t('ui.list.delete')}
        onClick={() => window.dispatchEvent(new CustomEvent('tsk-delete-project', { detail: { id: project.id, name: project.name } }))}
      >
        <FontAwesomeIcon icon={['fas', 'trash-alt']} />
      </button>
    );
  }

  // Carte d'une interface. `archived` => bouton Restaurer, sinon Dupliquer + Archiver.
  renderRow(project, archived) {
    const devices = project.devices || [];
    const deployed = devices.length > 0;
    const offline = devices.filter((d) => !d.connected).length;
    const isFav = this.state.favorites.includes(project.id);
    const initials = (project.name || '?').trim().slice(0, 2).toUpperCase();
    const hue = Array.from(project.name || '?').reduce((h, c) => (h * 31 + c.charCodeAt(0)) % 360, 7);
    const refetchQueries = [{ query: queries.projects.list }, { query: queries.projects.archived }];
    return (
      <li key={project.id} className='tsk-project-card'>
        <Link to={`/project/${project.id}`} className='tsk-project-card__main' onClick={() => touchRecent(project.id)}>
          <span
            className='tsk-project-card__avatar'
            style={{ background: `linear-gradient(135deg, hsl(${hue}, 78%, 62%), hsl(${(hue + 40) % 360}, 72%, 48%))` }}
          >
            {initials}
          </span>
          <span className='tsk-project-card__name'>{project.name}</span>
          <span className={`tsk-badge ${!deployed ? 'tsk-badge--idle' : offline > 0 ? 'tsk-badge--warn' : 'tsk-badge--ok'}`}>
            <span className='tsk-badge__dot' />
            {!deployed
              ? t('ui.list.not_deployed')
              : offline === 0
                ? tn('ui.list.screens_ok', devices.length)
                : tn('ui.list.screens_off', devices.length, { off: offline })}
          </span>
          <span className='tsk-project-card__devices'>{deployed ? deploymentLabel(project) : t('ui.list.no_screen')}</span>
          <span className='tsk-project-card__open'>
            {t('ui.list.open')} <FontAwesomeIcon icon={['fas', 'arrow-right']} />
          </span>
        </Link>
        <button
          type='button'
          className={`tsk-project-card__fav${isFav ? ' is-fav' : ''}`}
          title={isFav ? t('ui.list.fav_remove') : t('ui.list.fav_add')}
          aria-pressed={isFav}
          onClick={(e) => this.onToggleFavorite(e, project.id)}
        >
          <FontAwesomeIcon icon={['fas', 'star']} />
        </button>
        <NeedAdmin trueAdmin>
          <div className='tsk-project-card__actions'>
            {archived ? (
              <>
                <Mutation mutation={queries.projects.restore} refetchQueries={refetchQueries} onCompleted={() => showToast(t('ui.list.restored'))}>
                  {(restore) => (
                    <button
                      type='button'
                      className='tsk-button tsk-small'
                      onClick={() => restore({ variables: { input: project.id } })}
                    >
                      <FormattedMessage id='projects.restore.button' defaultMessage='Restaurer' />
                    </button>
                  )}
                </Mutation>
                {this.deleteButton(project)}
              </>
            ) : (
              <>
                <Mutation mutation={queries.projects.duplicate} refetchQueries={refetchQueries} onCompleted={() => showToast(t('ui.list.duplicated'))}>
                  {(duplicate) => (
                    <button
                      type='button'
                      className='tsk-button tsk-small tsk-projects-icon-btn'
                      title={t('ui.list.duplicate')}
                      onClick={() => {
                        confirmDialog(
                          <FormattedMessage id='project.duplicate.confirmation.dialog.message' />
                        ).then(() => duplicate({ variables: { input: project.id } }));
                      }}
                    >
                      <FontAwesomeIcon icon={['fas', 'copy']} />
                    </button>
                  )}
                </Mutation>
                <Mutation mutation={queries.projects.archive} refetchQueries={refetchQueries} onCompleted={() => showToast(t('ui.list.archived'), 'info')}>
                  {(archive) => (
                    <button
                      type='button'
                      className='tsk-button tsk-small tsk-projects-icon-btn tsk-projects-archive-btn'
                      title={t('ui.list.archive')}
                      onClick={() => {
                        confirmDialog(
                          <FormattedMessage id='projects.archive.confirm' defaultMessage='Archiver cette interface ?' />
                        ).then(() => archive({ variables: { input: project.id } }));
                      }}
                    >
                      <FontAwesomeIcon icon={['fas', 'archive']} />
                    </button>
                  )}
                </Mutation>
                {this.deleteButton(project)}
              </>
            )}
          </div>
        </NeedAdmin>
      </li>
    );
  }

  renderStats(projects) {
    const online = projects.filter(isDeployed);
    const screens = projects.reduce((n, p) => n + (p.devices || []).length, 0);
    const connected = projects.reduce((n, p) => n + (p.devices || []).filter((d) => d.connected).length, 0);
    const tiles = [
      { icon: 'folder-open', value: projects.length, label: t('ui.list.stat.total') },
      { icon: 'check-circle', value: online.length, label: t('ui.list.stat.online'), tone: 'ok' },
      { icon: 'pause-circle', value: projects.length - online.length, label: t('ui.list.stat.idle'), tone: 'idle' },
      { icon: 'desktop', value: `${connected}/${screens}`, label: t('ui.list.stat.screens') },
    ];
    return (
      <div className='tsk-stats'>
        {tiles.map((tile) => (
          <div key={tile.label} className={`tsk-stat${tile.tone ? ` tsk-stat--${tile.tone}` : ''}`}>
            <span className='tsk-stat__icon'><FontAwesomeIcon icon={['fas', tile.icon]} /></span>
            <span className='tsk-stat__value'>{tile.value}</span>
            <span className='tsk-stat__label'>{tile.label}</span>
          </div>
        ))}
      </div>
    );
  }

  render() {
    const { showArchived, search, status, sort, favorites, visible } = this.state;
    const query = showArchived ? queries.projects.archived : queries.projects.list;

    return (
      <div className="tsk-page-board">
        <header className="tsk-page-hero">
          <h1 className="tsk-page-hero__title">
            <FormattedMessage id="projects.title" />
          </h1>
          <p className="tsk-page-hero__subtitle">
            {t('ui.list.lead')}
          </p>
        </header>

        <Query query={query} fetchPolicy='cache-and-network'>
          {({ loading, error, data }) => {
            // Pendant le chargement, react-apollo fournit `data = {}` : on attend que la liste soit réellement arrivée.
            const loadedList = data && (showArchived ? data.archivedProjects : data.projects);
            if (!loadedList && !error) {
              return (
                <div aria-busy='true' aria-label={t('ui.common.loading')}>
                  <div className='tsk-skeleton-stats'>{[0, 1, 2, 3].map((i) => <span key={i} className='tsk-skeleton' />)}</div>
                  <div className='tsk-skeleton-grid'>{[0, 1, 2].map((i) => <span key={i} className='tsk-skeleton' />)}</div>
                  <LoadingHint />
                </div>
              );
            }
            if (error) return t('ui.list.error', { message: error.message });

            const projects = loadedList || [];
            const lowerSearch = search.toLowerCase();
            const recents = showArchived
              ? []
              : getRecents().map((id) => projects.find((p) => p.id === id)).filter(Boolean).slice(0, 4);

            const filtered = projects
              .filter((p) => {
                if (status === 'online' && !isDeployed(p)) return false;
                if (status === 'idle' && isDeployed(p)) return false;
                if (!lowerSearch) return true;
                return (
                  (p.name && p.name.toLowerCase().includes(lowerSearch)) ||
                  deploymentLabel(p).toLowerCase().includes(lowerSearch)
                );
              })
              .slice()
              .sort((a, b) => {
                const favDiff = Number(favorites.includes(b.id)) - Number(favorites.includes(a.id));
                if (favDiff) return favDiff;
                if (sort === 'status' && isDeployed(a) !== isDeployed(b)) return isDeployed(b) - isDeployed(a);
                if (sort === 'screens') {
                  const diff = (b.devices || []).length - (a.devices || []).length;
                  if (diff) return diff;
                }
                return (a.name || '').localeCompare(b.name || '');
              });

            return (
              <>
                {!showArchived && this.renderStats(projects)}

                {recents.length > 0 && !search && (
                  <section className='tsk-recents'>
                    <h2 className='tsk-section-title'>{t('ui.list.recents')}</h2>
                    <div className='tsk-recents__row'>
                      {recents.map((p) => (
                        <Link key={p.id} to={`/project/${p.id}`} className='tsk-recents__chip' onClick={() => touchRecent(p.id)}>
                          <FontAwesomeIcon icon={['fas', 'file-alt']} /> {p.name}
                        </Link>
                      ))}
                    </div>
                  </section>
                )}

                <div className="tsk-displays-toolbar">
                  <input
                    className='tsk-input-search'
                    type='text'
                    placeholder={t('ui.list.search')}
                    value={search}
                    onChange={(e) => this.setState({ search: e.target.value, visible: PAGE_SIZE })}
                  />
                  <div className='tsk-segmented' role='group' aria-label={t('ui.list.status_aria')}>
                    {STATUS_FILTERS.map((f) => (
                      <button
                        key={f.id}
                        type='button'
                        className={status === f.id ? 'is-active' : ''}
                        onClick={() => this.setState({ status: f.id, visible: PAGE_SIZE })}
                      >
                        {f.label}
                      </button>
                    ))}
                  </div>
                  <select
                    className='tsk-sort-select'
                    value={sort}
                    aria-label={t('ui.list.sort_aria')}
                    onChange={(e) => this.setState({ sort: e.target.value, visible: PAGE_SIZE })}
                  >
                    {SORTS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                  </select>
                  <NeedAdmin trueAdmin>
                    <button
                      type='button'
                      className='tsk-button tsk-displays-toolbar__back tsk-toolbar-push'
                      onClick={() => this.setState({ showArchived: !showArchived, status: 'all', visible: PAGE_SIZE })}
                    >
                      <FontAwesomeIcon icon={['fas', showArchived ? 'folder-open' : 'archive']} />{' '}
                      {showArchived
                        ? <FormattedMessage id='projects.show.active' defaultMessage='Interfaces actives' />
                        : <FormattedMessage id='projects.show.archived' defaultMessage='Voir les archivées' />}
                    </button>
                  </NeedAdmin>
                </div>

                {filtered.length === 0 ? (
                  <div className='tsk-empty'>
                    <FontAwesomeIcon icon={['fas', 'folder-open']} className='tsk-empty__icon' />
                    <h3>{search || status !== 'all' ? t('ui.list.no_result') : showArchived ? t('ui.list.no_archived') : t('ui.list.no_interface')}</h3>
                    <p>{search || status !== 'all' ? t('ui.list.try_other') : t('ui.list.create_first')}</p>
                    {!showArchived && !search && status === 'all' && (
                      <Link to='/project/new' className='tsk-empty__cta'>
                        <FontAwesomeIcon icon={['fas', 'plus']} /> {t('ui.list.new_btn')}
                      </Link>
                    )}
                  </div>
                ) : (
                  <>
                    <ul className='tsk-projects-list-container'>
                      {filtered.slice(0, visible).map((project) => this.renderRow(project, showArchived))}
                    </ul>
                    {filtered.length > visible && (
                      <div className='tsk-list-more'>
                        <span>{t('ui.list.showing', { shown: visible, total: filtered.length })}</span>
                        <button type='button' className='tsk-button' onClick={() => this.setState({ visible: visible + PAGE_SIZE })}>
                          {t('ui.list.more', { count: Math.min(PAGE_SIZE, filtered.length - visible) })}
                        </button>
                        <button type='button' className='tsk-button tsk-link' onClick={() => this.setState({ visible: filtered.length })}>
                          {t('ui.list.all')}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </>
            );
          }}
        </Query>
      </div>
    );
  }
}

export default ProjectsList;
